import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { Link, Paragraph, Text } from '@rover/kibble/core';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import Guarantees from '../../components/Guarantees';
import ServiceLandingSection from '../../components/ServiceLandingSection';
import ServiceLandingServices from '../../components/ServiceLandingServices';
import ServiceTargetAudience from '../../components/ServiceTargetAudience';
import ServiceValueProposition from '../../components/ServiceValueProposition';
import puppyBoardingHeroImageDesktop from '../shared-images/Hero_Puppy_desktop.jpg';
import puppyBoardingHeroImageMobile from '../shared-images/Hero_Puppy_mobile.jpg';
import puppyBoardingHeroImageTablet from '../shared-images/Hero_Puppy_tablet.jpg';

const puppyBoardingImages: HeroImageSrcSet = {
  desktop: {
    src: puppyBoardingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: puppyBoardingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: puppyBoardingHeroImageMobile,
    height: 614,
  },
};

const PuppyBoarding: React.FC<Props> = ({
  locale,
  countryConfig,
  serviceLinksWithDisplayName,
  serviceLandingData,
  citiesAndNeighborhoods,
  searchResults,
  serviceTypes,
}) => {
  const { t } = useLingui();
  const serviceLandingType = ServiceLandingEnum.PUPPY_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
  // Tags analytics events with the real booking service type rather than the SEO-slug vocabulary
  // serviceTypeSlug above uses (e.g. 'overnight-boarding', not 'dog-boarding').
  const internalServiceTypeSlug = SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet
        title={seoTitle}
        metaDescription={seoMetaDescription}
        indexable={serviceLandingData.indexable}
      />
      <LandingHero mb="20x" heroImageSrcSet={puppyBoardingImages}>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            locale={locale}
            serviceTypes={serviceTypes}
            internalServiceType={{
              slug: internalServiceTypeSlug,
              startDateSelectorDescription: t`Drop off`,
              endDateSelectorDescription: t`Pick up`,
            }}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot locale={locale} />
        </LandingHero.Content>
      </LandingHero>

      <ServiceLandingSection>
        <ServiceValueProposition>
          <ServiceValueProposition.Display>
            <Trans>Find affordable long-term puppy boarding</Trans>
          </ServiceValueProposition.Display>
          <ServiceValueProposition.List>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Affordable, personalized puppy care</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  We make it easy to search for a sitter who is more affordable than a kennel. Find
                  out{' '}
                  <Link href="/dog-sitting-rates/" target="_blank">
                    how much
                  </Link>{' '}
                  puppy sitters near you typically cost.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Puppy sitters you can trust</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Every sitter on the site has been reviewed and approved by the Rover team, and you
                  can easily search for sitters who offer puppy care.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Convenient Meet & Greets</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Meet your puppy’s perfect match before you book. Ask any sitters you’re interested
                  in to set up a free Meet & Greet.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Cute photo updates</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Get photos of your puppy’s clumsy steps, snuggles with their sitter, and evidence
                  that they’re having a blast.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
          </ServiceValueProposition.List>
        </ServiceValueProposition>
      </ServiceLandingSection>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>Meet local sitters who will treat your puppy like family</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                Read verified reviews by pet parents like you and choose a sitter who’s a great
                match for you and your puppy.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Book and Pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                No cash or checks needed—we make it simple to book and make secured payments through
                our website or app.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Relax</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                Your puppy is in great hands with a loving sitter. Plus, your booking is backed by{' '}
                <Link href={countryConfig.urls.supportUrl} target="_blank">
                  24/7 support
                </Link>{' '}
                and the{' '}
                <Link href={countryConfig.roverGuaranteeUrl} target="_blank">
                  Rover Guarantee
                </Link>
                .
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      <ServiceLandingSection>
        <ServiceTargetAudience>
          <ServiceTargetAudience.Display>
            <Trans>Puppy sitting that works for you, your puppy, and your budget</Trans>
          </ServiceTargetAudience.Display>
          <ServiceTargetAudience.Content>
            <Trans>
              Founded in 2011, Rover makes it easy for you to connect with a loving puppy sitter in
              your neighborhood. If you're looking for someone who can keep up with your
              puppy—without breaking the bank—you're in the right place.
            </Trans>
          </ServiceTargetAudience.Content>
        </ServiceTargetAudience>
      </ServiceLandingSection>

      <ServiceLandingSection>
        <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />
      </ServiceLandingSection>

      <ServiceLandingSection>
        <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
          <ServiceLandingServices.Display>
            <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
          </ServiceLandingServices.Display>
        </ServiceLandingServices>

        <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
          <CitiesAndNeighborhoods.Display>
            <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
          </CitiesAndNeighborhoods.Display>
        </CitiesAndNeighborhoods>
      </ServiceLandingSection>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>
    </>
  );
};

export default PuppyBoarding;
